import type { Context, ServiceMap } from '@tnega/core'
import { randomUUID } from 'node:crypto'

import {
  DEFAULT_CONTEXT_COMPACT_RATIO,
  DEFAULT_CONTEXT_HARD_RATIO,
  DEFAULT_CONTEXT_LIMIT,
  DEFAULT_CONTEXT_RETAIN_RATIO,
  estimateContextUsage,
  type AssistantMessagePayload,
  type AssistantStreamChunk,
  type AssistantStreamRecord,
  type ModelMessage,
  type ModelUsage,
  renderToolResult,
  TOOL_OUTPUT_META_KIND,
  type RequestContextPayload,
  type SessionLog,
  type ToolResultPayload,
  type TurnEndReason,
} from '@tnega/session'
import { toToolError, type ToolDefinition, type ToolError, type ToolResult } from '@tnega/tools'
import type { ToolSchemaSnapshot } from './prompt.js'
import type { ToolsService } from '@tnega/tools'

import type {
  AgentContextBudget,
  AgentAssistantStreamEvent,
  AgentCancelCause,
  AgentFinishReason,
  AgentHooks,
  AgentInput,
  AgentNextStepClaimer,
  AgentPreStepEvent,
  AgentRequestErrorEvent,
  AgentRequestEvent,
  AgentRequestRetryDecision,
  AgentRunOptions,
  AgentRunResult,
  AgentStep,
  AgentStepEndEvent,
  AgentStepLimitsEvent,
  AgentStreamEvent,
  LLMStreamRequestEvent,
  AgentTurnStoppingEvent,
  CompleteOptions,
  LLMAdapter,
  LLMCompletion,
  LLMStreamEvent,
  LLMToolCall,
} from './types.js'

export class AgentError extends Error {
  override name = 'AgentError'
}

export class AgentInbox {
  private _queue: AgentInput[] = []
  private _steerQueue: AgentInput[] = []
  private _injected = new Map<string, unknown>()

  get size(): number {
    return this._queue.length + this._steerQueue.length
  }

  get steerSize(): number {
    return this._steerQueue.length
  }

  push(input: AgentInput): AgentInput {
    this._queue.push(input)
    return input
  }

  followup(input: AgentInput): AgentInput {
    return this.push(input)
  }

  steer(input: AgentInput): AgentInput {
    this._steerQueue.push(input)
    return input
  }

  send(
    input: AgentInput,
    options: { mode?: 'followup' | 'steer'; wake?: boolean } = {},
  ): AgentInput {
    if (options.mode === 'steer') return this.steer(input)
    return this.followup(input)
  }

  claim(): AgentInput | undefined {
    return this._steerQueue.shift() ?? this._queue.shift()
  }

  peek(): AgentInput | undefined {
    return this._steerQueue[0] ?? this._queue[0]
  }

  inject(key: string, value: unknown): void {
    this._injected.set(key, value)
  }

  injected(): ReadonlyMap<string, unknown> {
    return new Map(this._injected)
  }
}

export interface AgentConfig {
  llm?: LLMAdapter
  /** Capacity of the selected model, recorded with each request. */
  contextWindow?: number
  agentId?: string
  /** Bind the loop to a specific session instead of the ctx-provided singleton. */
  session?: SessionLog
  /** Compatibility diagnostic flag; request replayability is always enforced. */
  assertReplayable?: boolean
  maxTurns?: number
  maxSteps?: number
  inbox?: AgentInbox
  hooks?: AgentHooks
  contextBudget?: AgentContextBudget
  /** Optional live-agent seam for work admitted after the first step. */
  claimNextStep?: AgentNextStepClaimer
}

function copyMessages(messages: readonly ModelMessage[]): ModelMessage[] {
  return messages.map((message) => {
    const copy: ModelMessage = {
      role: message.role,
      content: message.content,
    }
    if (message.reasoning) copy.reasoning = message.reasoning
    if (message.name) copy.name = message.name
    if (message.tool_call_id) copy.tool_call_id = message.tool_call_id
    if (message.toolOk !== undefined) copy.toolOk = message.toolOk
    if (message.toolError) copy.toolError = { ...message.toolError }
    if (message.attachments?.length) copy.attachments = message.attachments.map(item => ({ ...item }))
    if (message.tool_calls) {
      copy.tool_calls = message.tool_calls.map(call => ({
        id: call.id,
        name: call.name,
        arguments: structuredClone(call.arguments),
      }))
    }
    return copy
  })
}

function isUserOrSystemMessage(
  message: ModelMessage,
): message is ModelMessage & { role: 'system' | 'user' } {
  return message.role === 'system' || message.role === 'user'
}

function stringify(value: unknown): string {
  if (typeof value === 'string') return value
  try {
    return JSON.stringify(value) ?? String(value)
  } catch {
    return String(value)
  }
}

function copyCompletion(completion: LLMCompletion): LLMCompletion {
  const copy: LLMCompletion = {
    finishReason: completion.finishReason,
  }
  if (completion.content !== undefined) copy.content = completion.content
  if (completion.reasoning !== undefined) copy.reasoning = completion.reasoning
  if (completion.toolCalls) {
    copy.toolCalls = completion.toolCalls.map(call => ({
      id: call.id,
      name: call.name,
      arguments: call.arguments,
    }))
  }
  return copy
}

function canonicalMessages(messages: readonly ModelMessage[]): string {
  return JSON.stringify(messages.map(message => ({
    role: message.role,
    content: message.content,
    ...(message.reasoning ? { reasoning: message.reasoning } : {}),
    ...(message.name ? { name: message.name } : {}),
    ...(message.tool_call_id ? { tool_call_id: message.tool_call_id } : {}),
    ...(message.tool_calls?.length ? { tool_calls: message.tool_calls } : {}),
    ...(message.toolOk !== undefined ? { toolOk: message.toolOk } : {}),
    // Fingerprint rather than the full base64 so the per-step replay check stays cheap.
    ...(message.attachments?.length ? {
      attachments: message.attachments.map(item => `${item.mediaType}:${item.data.length}:${item.data.slice(-32)}`),
    } : {}),
    ...(message.toolError ? {
      toolError: { name: message.toolError.name, message: message.toolError.message },
    } : {}),
  })))
}

function freezeRequestData(data: unknown): void {
  const visited = new Set<object>()
  const freeze = (value: unknown): void => {
    if (typeof value !== 'object' || value === null || visited.has(value)) return
    visited.add(value)
    for (const child of Object.values(value)) freeze(child)
    Object.freeze(value)
  }
  freeze(data)
}

function lockStreamRequest(request: LLMStreamRequestEvent): void {
  freezeRequestData(request.messages)
  request.tools = Object.freeze(request.tools.map(tool => {
    // Tool definitions belong to the registry. Lock a request-local schema
    // snapshot so later requests can still update the registered definition.
    const schema = structuredClone(tool.schema)
    freezeRequestData(schema)
    return Object.freeze({ ...tool, schema })
  }))
  // The signal remains live; only the request's option bindings are locked.
  Object.freeze(request.options)
  Object.freeze(request)
}

function requestHeaderFor(
  request: Pick<AgentRequestEvent, 'tools' | 'options'>,
  input: readonly ModelMessage[],
): {
  config?: { provider?: string; model?: string; temperature?: number; reasoningEffort?: string }
  system?: string
  tools?: ToolSchemaSnapshot[]
} {
  const system = input.find(message => message.role === 'system')?.content
  const tools = request.tools.map(tool => ({
    name: tool.schema.name,
    description: tool.schema.description,
    ...(tool.schema.parameters ? { parameters: tool.schema.parameters } : {}),
  }))
  const config = {
    ...(request.options.provider ? { provider: request.options.provider } : {}),
    ...(request.options.model ? { model: request.options.model } : {}),
    ...(request.options.temperature !== undefined
      ? { temperature: request.options.temperature }
      : {}),
    ...(request.options.reasoningEffort ? { reasoningEffort: request.options.reasoningEffort } : {}),
  }
  return {
    ...(Object.keys(config).length ? { config } : {}),
    ...(system !== undefined ? { system } : {}),
    ...(tools.length ? { tools } : {}),
  }
}

/** A completion played back as stream events, for an adapter that cannot stream. */
export async function* completeAsStream(
  llm: LLMAdapter,
  messages: readonly ModelMessage[],
  tools: readonly ToolDefinition[],
  options: CompleteOptions,
): AsyncGenerator<LLMStreamEvent, void, void> {
  const id = randomUUID()
  yield { type: 'message_start', id }
  const completion = await llm.complete(messages, tools, options)
  if (completion.reasoning) yield { type: 'reasoning_delta', id, delta: completion.reasoning }
  if (completion.content) yield { type: 'message_delta', id, delta: completion.content }
  for (let index = 0; index < (completion.toolCalls?.length ?? 0); index += 1) {
    const call = completion.toolCalls![index]!
    yield { type: 'toolcall_start', id: call.id, index, name: call.name }
    yield {
      type: 'toolcall_end',
      id: call.id,
      index,
      name: call.name,
      arguments: call.arguments,
    }
  }
  yield {
    type: 'message_stop',
    id,
    finishReason: completion.finishReason,
    ...(completion.usage ? { usage: completion.usage } : {}),
  }
}

function isCancelCause(value: unknown): value is AgentCancelCause {
  if (typeof value !== 'object' || value === null) return false
  const record = value as Record<string, unknown>
  if (record.type === 'user' || record.type === 'parent' || record.type === 'disposed') {
    return true
  }
  if (
    record.type === 'abort'
    && (record.message === undefined || typeof record.message === 'string')
  ) {
    return true
  }
  if (record.type === 'timeout' && typeof record.timeoutMs === 'number') {
    return true
  }
  return false
}

function cancelCauseFromSignal(signal?: AbortSignal): AgentCancelCause | undefined {
  if (!signal?.aborted) return undefined
  const reason = signal.reason
  if (isCancelCause(reason)) return reason
  return { type: 'abort' }
}

function abortedToolError(signal?: AbortSignal): ToolError {
  const cause = cancelCauseFromSignal(signal)
  return {
    name: 'AbortError',
    message: `tool call aborted: ${cause?.type ?? 'abort'}`,
  }
}

/** Map a completed loop to the typed durable reason a `turn/end` records. */
function toTurnEndReason(fields: {
  cancelled: boolean
  turnError: unknown
  finishReason: AgentFinishReason
  cancelCause?: AgentCancelCause
}): TurnEndReason {
  const { cancelled, turnError, finishReason, cancelCause } = fields
  if (cancelled || finishReason === 'cancelled') {
    return { kind: 'aborted', cause: cancelCause ?? { type: 'user' } }
  }
  if (turnError || finishReason === 'error') {
    return {
      kind: 'error',
      error: turnError
        ? toToolError(turnError)
        : { name: 'TurnError', message: finishReason },
    }
  }
  switch (finishReason) {
    case 'length':
      return { kind: 'max-tokens' }
    case 'max_steps':
      return { kind: 'max-steps' }
    case 'max_turns':
      return { kind: 'max-turns' }
    default:
      return { kind: 'completed' }
  }
}

function partialStreamContent(events: readonly LLMStreamEvent[]): string {
  let content = ''
  for (const event of events) {
    if (event.type === 'message_delta') content += event.delta
  }
  return content
}

async function cancellableDelay(ms: number, signal?: AbortSignal): Promise<boolean> {
  if (ms <= 0) return !signal?.aborted
  if (signal?.aborted) return false
  return new Promise(resolve => {
    const timer = setTimeout(() => {
      signal?.removeEventListener('abort', onAbort)
      resolve(true)
    }, ms)
    const onAbort = (): void => {
      clearTimeout(timer)
      resolve(false)
    }
    signal?.addEventListener('abort', onAbort, { once: true })
  })
}

/** Owns accumulation, transient framing and exactly one durable settlement. */
class AssistantStreamAttempt {
  private readonly attemptId = randomUUID()
  private readonly records: AssistantStreamRecord[] = []
  private ended = false

  constructor(
    private readonly session: SessionLog,
    private readonly turn: number,
    private readonly step: number,
    private readonly nextRevision: () => number,
  ) {}

  start(): AgentAssistantStreamEvent {
    return { type: 'assistant/stream', frame: {
      type: 'start', attemptId: this.attemptId, revision: this.nextRevision(),
      turn: this.turn, step: this.step,
    } }
  }

  push(chunk: AssistantStreamChunk): AgentAssistantStreamEvent {
    if (this.ended) throw new AgentError('assistant attempt is already settled')
    const record = { time: Date.now(), chunk: structuredClone(chunk) }
    const index = this.records.length
    this.records.push(record)
    return { type: 'assistant/stream', frame: {
      type: 'chunk', attemptId: this.attemptId, revision: this.nextRevision(), index,
      ...structuredClone(record),
    } }
  }

  async settle(message?: AssistantMessagePayload): Promise<AgentAssistantStreamEvent> {
    if (this.ended) throw new AgentError('assistant attempt is already settled')
    const eventType = message ? 'assistant/message' : 'assistant/attempt'
    let event
    try {
      event = message
        ? await this.session.append('assistant/message', { ...message, stream: this.records })
        : await this.session.append('assistant/attempt', { turn: this.turn, step: this.step, stream: this.records })
    } catch (cause) {
      this.ended = true
      const frame: AgentAssistantStreamEvent = { type: 'assistant/stream', frame: {
        type: 'end', attemptId: this.attemptId, revision: this.nextRevision(), index: this.records.length,
        outcome: { kind: 'abandoned' },
      } }
      throw new AttemptSettlementError(cause, frame)
    }
    this.ended = true
    return { type: 'assistant/stream', frame: {
      type: 'end', attemptId: this.attemptId, revision: this.nextRevision(), index: this.records.length,
      outcome: { kind: 'committed', eventType, seq: event.seq },
    } }
  }
}

class AttemptSettlementError extends Error {
  constructor(override readonly cause: unknown, readonly frame: AgentAssistantStreamEvent) {
    super('assistant attempt settlement failed')
  }
}

export class AgentService {
  private async *settleAttempt(
    attempt: AssistantStreamAttempt,
    message?: AssistantMessagePayload,
  ): AsyncGenerator<AgentAssistantStreamEvent, void, void> {
    try {
      yield await attempt.settle(message)
    } catch (error) {
      if (error instanceof AttemptSettlementError) {
        yield error.frame
        throw error.cause
      }
      throw error
    }
  }

  readonly inbox: AgentInbox
  private _seriesStarted = false
  private _persistedRequest = false
  private _streamRevision = 0

  private _attempt(session: SessionLog, turn: number, step: number): AssistantStreamAttempt {
    return new AssistantStreamAttempt(session, turn, step, () => ++this._streamRevision)
  }

  constructor(
    private ctx: Context,
    private config: AgentConfig = {},
  ) {
    this.inbox = config.inbox ?? new AgentInbox()
  }

  /** Call when a request begins an explicit new model-message series. */
  startSeries(): void {
    this._seriesStarted = true
  }

  /** Clear any pending series marker after a completed run. */
  endSeriesBoundary(): void {
    this._seriesStarted = false
  }

  /** True when this service instance has already persisted a model-visible step. */
  get hasPersistedRequest(): boolean {
    return this._persistedRequest
  }

  async run(input?: AgentInput, options: AgentRunOptions = {}): Promise<AgentRunResult> {
    return consumeAgentStream(this._stream(input, options))
  }

  async *runStream(
    input?: AgentInput,
    options: AgentRunOptions = {},
  ): AsyncGenerator<AgentStreamEvent, AgentRunResult, void> {
    const iterator = this._stream(input, options)[Symbol.asyncIterator]()
    while (true) {
      const next = await iterator.next()
      if (next.done) return next.value
      yield next.value
    }
  }

  private async *_stream(
    input?: AgentInput,
    options: AgentRunOptions = {},
  ): AsyncGenerator<AgentStreamEvent, AgentRunResult, void> {
    const claimed = input ?? this.inbox.claim()
    if (!claimed) throw new AgentError('no agent input available')
    await this.config.hooks?.beforeRun?.(claimed, options)

    const session = this._session()
    const tools = this._tools()
    const llm = this._llm()
    if (!llm) throw new AgentError('no LLM adapter available')
    const streamAdapter = llm.stream
    const persistChunks = streamAdapter !== undefined

    const requestedLimits: AgentStepLimitsEvent = {
      ...(this.config.agentId ? { agentId: this.config.agentId } : {}),
    }
    const requestedTurns = options.maxTurns ?? this.config.maxTurns
    const requestedSteps = options.maxSteps ?? this.config.maxSteps
    if (requestedTurns !== undefined) requestedLimits.maxTurns = requestedTurns
    if (requestedSteps !== undefined) requestedLimits.maxSteps = requestedSteps
    const limits = await this.ctx.waterfallAsync(
      'agent/step-limits', requestedLimits, (payload: AgentStepLimitsEvent) => payload,
    ) as AgentStepLimitsEvent | undefined
    const maxTurns = limits?.maxTurns ?? requestedTurns ?? 64
    const maxSteps = limits?.maxSteps ?? requestedSteps ?? 64
    const contextBudget = options.contextBudget ?? this.config.contextBudget
    const injected = this.inbox.injected()
    this.ctx.emit('agent/start', { input: claimed, options, injected })

    let messages = await this._initialMessages(claimed)
    let claimedMessages = messages.filter(message => message.role === 'user')
    const steps: AgentStep[] = []
    let output = ''
    let finishReason: AgentFinishReason = 'stop'
    let lengthReached = false

    this.ctx.emit('agent/turn-start', {
      ...(this.config.agentId ? { agentId: this.config.agentId } : {}),
      input: claimed,
      messages: copyMessages(messages),
      injected,
    })

    const turn = options.turn ?? await session.nextTurn()
    if (options.turn === undefined) {
      await session.append('turn/start', {
        turn,
        input: claimed.text ?? claimed,
        reason: 'user',
      })
    }

    let index = 0
    // Whether this run was judged near its end when it crossed the compact ratio.
    const budgetState: { nearEnd?: boolean } = {}
    let finalTurnGranted = false
    // Whether the step budget already handed the plugins one extra step.
    let budgetGranted = false
    let currentStepIndex: number | undefined
    let turnError: unknown
    try {
      while (index < maxTurns + (finalTurnGranted ? 1 : 0)) {
      if (options.signal?.aborted) {
        finishReason = 'cancelled'
        break
      }
      if (steps.length >= maxSteps) {
        // Out of steps while the model was still working. Ask exactly as a
        // stopping step does: a plugin may steer one last step out of it (the
        // continuation nudge does), so a run that was cut mid-task reports
        // instead of ending on tool results nobody read.
        const continued = budgetGranted ? undefined : await this._requestContinuation({
          ...(this.config.agentId ? { agentId: this.config.agentId } : {}),
          index,
          turn,
          steps: copySteps(steps),
          finishReason: 'max_steps',
        })
        if (!continued) {
          finishReason = 'max_steps'
          break
        }
        budgetGranted = true
        if (continued.length) {
          messages = [...messages, ...continued]
          claimedMessages = continued.filter(message => message.role === 'user')
        }
      }
      if (contextBudget) {
        messages = await this._enforceContextBudget(session, contextBudget, messages, llm, index, budgetState)
      }

      const stepInput = copyMessages(messages)
      const preStep = await this.ctx.waterfallAsync('agent/pre-step', {
        ...(this.config.agentId ? { agentId: this.config.agentId } : {}),
        index,
        turn,
        step: index,
        messages: stepInput,
        claimedMessages: copyMessages(claimedMessages),
        ...(options.signal ? { signal: options.signal } : {}),
      }, (payload: AgentPreStepEvent) => payload)
      if (
        !preStep
        || preStep.admission === 'reject'
        || !Array.isArray(preStep.messages)
        || !preStep.messages.length
      ) {
        break
      }
      if (preStep.startsRequestSeries) this._seriesStarted = true

      await session.append('step/start', { turn, step: index })
      currentStepIndex = index

      const requestedInput = copyMessages(preStep.messages)
      // Request rewrites may replace the current surface, but retries retain
      // the durable owners present when this step was admitted.
      const admittedHistory = await session.deriveMessages()
      this.ctx.emit('agent/step', {
        index,
        turn,
        step: index,
        input: copyMessages(requestedInput),
      })
      const availableTools = finalTurnGranted ? [] : await this._resolveAvailableTools()
      const completeOptions: CompleteOptions = {
        maxSteps: maxSteps - steps.length,
      }
      if (this.config.contextWindow !== undefined) completeOptions.contextWindow = this.config.contextWindow
      if (options.signal) completeOptions.signal = options.signal

      let llmMessages = copyMessages(requestedInput)

      let completion: LLMCompletion | undefined
      let activeAttempt: AssistantStreamAttempt | undefined
      // When the request that produced `completion` started, for throughput.
      let requestStartedAt = 0
      let attempt = 0
      /**
       * Cost and latency of the response being committed. Only a real
       * measurement is reported: a response that never started a request
       * carries no duration, and a provider that reported no usage carries
       * none, rather than a zero that would read as a free, instant reply.
       */
      const settledMeta = (): { reasoning?: string; durationMs?: number; usage?: ModelUsage } => {
        const meta: { reasoning?: string; durationMs?: number; usage?: ModelUsage } = {}
        if (completion?.reasoning) meta.reasoning = completion.reasoning
        if (requestStartedAt > 0) meta.durationMs = Date.now() - requestStartedAt
        if (completion?.usage) meta.usage = completion.usage
        return meta
      }
      while (true) {
        if (options.signal?.aborted) {
          finishReason = 'cancelled'
          break
        }
        const streamEvents: LLMStreamEvent[] = []
        activeAttempt = this._attempt(session, turn, index)
        yield activeAttempt.start()
        const streamRequest: LLMStreamRequestEvent = {
          index,
          messages: copyMessages(requestedInput),
          tools: [...availableTools],
          options: { ...completeOptions },
        }
        try {
          const request = await this.ctx.waterfallAsync('agent/request', {
            ...(this.config.agentId ? { agentId: this.config.agentId } : {}),
            index,
            messages: copyMessages(requestedInput),
            tools: streamRequest.tools,
            options: streamRequest.options,
          }, (payload: AgentRequestEvent) => payload)
          if (!request || !request.options || !request.tools) {
            throw new AgentError('agent/request must return a request payload')
          }
          streamRequest.tools = request.tools
          streamRequest.options = request.options
          let preparation: Promise<void> | undefined
          const prepareRequest = (): Promise<void> => preparation ??= (async () => {
            // Waterfalls may rewrite the envelope until consumption begins.
            // Lazy streams must dispatch the same request that is persisted.
            lockStreamRequest(streamRequest)
            llmMessages = copyMessages(streamRequest.messages)
            await this._persistStepInput(
              session, streamRequest, llmMessages, admittedHistory,
              preStep.requestHeaderOwnsSystem === true,
            )
            await this._assertReplayable(session, streamRequest, llmMessages)
          })()
          const stream = await this.ctx.waterfallAsync(
            'llm/stream',
            streamRequest,
            (payload: LLMStreamRequestEvent) => (async function* () {
              // A listener can consume next() itself. Guard that dispatch too.
              await prepareRequest()
              yield* streamAdapter
                ? streamAdapter.call(llm, payload.messages, payload.tools, payload.options)
                : completeAsStream(llm, payload.messages, payload.tools, payload.options)
            })(),
          )
          if (!stream || typeof stream[Symbol.asyncIterator] !== 'function') {
            throw new AgentError('llm/stream must return an async iterable stream')
          }
          // Short-circuit streams must obey the same request invariant.
          await prepareRequest()
          requestStartedAt = Date.now()
          let chunkIndex = 0
          for await (const event of stream) {
            streamEvents.push(structuredClone(event))
            yield activeAttempt.push(event)
            if (persistChunks && event.type === 'message_delta') {
              await session.append('assistant/chunk', {
                id: event.id,
                content: event.delta,
                index: chunkIndex,
              })
              chunkIndex += 1
            }
            yield event
          }
          completion = completionFromStreamEvents(streamEvents)
          if (completion.finishReason === 'error' || completion.finishReason === 'cancelled') {
            const terminalReason = completion.finishReason
            completion = undefined
            throw new AgentError(`LLM stream finished with ${terminalReason}`)
          }
          break
        } catch (error) {
          yield activeAttempt.push({ type: 'stream_error', error: toToolError(error) })
          if (options.signal?.aborted) {
            const content = partialStreamContent(streamEvents)
            const reasoning = completionFromStreamEvents(streamEvents).reasoning
            yield* this.settleAttempt(
              activeAttempt,
              content ? { content, interrupted: true, ...(reasoning ? { reasoning } : {}), ...settledMeta() } : undefined,
            )
            finishReason = 'cancelled'
            break
          }
          yield* this.settleAttempt(activeAttempt)
          attempt += 1
          const failure = toToolError(error)
          const decision = await this.ctx.waterfallAsync(
            'agent/request-error',
            {
              index,
              turn,
              step: index,
              messages: copyMessages(streamRequest.messages),
              tools: [...streamRequest.tools],
              options: { ...streamRequest.options },
              attempt,
              error,
              failure,
              ...(streamRequest.options.provider ? { provider: streamRequest.options.provider } : {}),
              ...(streamRequest.options.model ? { model: streamRequest.options.model } : {}),
              ...(options.signal ? { signal: options.signal } : {}),
            } satisfies AgentRequestErrorEvent,
            () => undefined,
          ) as AgentRequestRetryDecision
          if (options.signal?.aborted) {
            finishReason = 'cancelled'
            break
          }
          if (decision?.kind !== 'retry') throw error
          const retryId = randomUUID()
          const delayMs = decision.delayMs ?? 0
          await session.append('llm/retry', {
            retryId,
            retry: attempt,
            ...(delayMs > 0 ? { delayMs } : {}),
            failure: toToolError(error),
          })
          if (!await cancellableDelay(delayMs, options.signal)) {
            finishReason = 'cancelled'
            break
          }
          await session.append('llm/retry-started', { retryId, retry: attempt })
        }
      }
      if (!completion || !activeAttempt) {
        if (options.signal?.aborted) {
          finishReason = 'cancelled'
          break
        }
        throw new AgentError('LLM adapter did not produce a completion')
      }

      const toolCalls = completion.toolCalls ?? []
      if (completion.finishReason === 'length') lengthReached = true
      if (options.signal?.aborted && toolCalls.length === 0) {
        yield activeAttempt.push({ type: 'stream_error', error: { name: 'AbortError', message: 'stream aborted' } })
        yield* this.settleAttempt(activeAttempt)
        finishReason = 'cancelled'
        break
      }
      if (finalTurnGranted && toolCalls.length && !options.signal?.aborted) {
        yield activeAttempt.push({ type: 'stream_error', error: { name: 'AgentError', message: 'maximum turns reached' } })
        yield* this.settleAttempt(activeAttempt)
        finishReason = 'max_turns'
        break
      }
      const toolResults: ToolResult[] = []
      if (toolCalls.length) {
        yield* this.settleAttempt(activeAttempt, {
          content: completion.content ?? '',
          toolCalls: toolCalls.map(call => ({
            id: call.id,
            name: call.name,
            arguments: call.arguments,
            interruption: availableTools.find(tool => tool.schema.name === call.name)?.interruption ?? 'fail',
          })),
          ...settledMeta(),
        })
      }
      for (const call of toolCalls) {
        this.ctx.emit('agent/tool-call', {
          ...(this.config.agentId ? { agentId: this.config.agentId } : {}),
          index, turn, step: index, call,
        })
        yield { type: 'tool/start', index, turn, step: index, call }
        await session.append('tool/call', {
          id: call.id,
          name: call.name,
          arguments: call.arguments,
          interruption: availableTools.find(tool => tool.schema.name === call.name)?.interruption ?? 'fail',
        })
        const recorder = new ToolOutputRecorder(session, call.id)
        const toolOptions: { callId: string; signal?: AbortSignal; agentId?: string; progress: (output: string) => void } = {
          callId: call.id,
          progress: output => recorder.push(output),
        }
        if (this.config.agentId) toolOptions.agentId = this.config.agentId
        if (options.signal) toolOptions.signal = options.signal
        const startedAt = Date.now()
        let result: ToolResult
        if (options.signal?.aborted) {
          result = {
            ok: false,
            name: call.name,
            callId: call.id,
            input: call.arguments,
            error: abortedToolError(options.signal),
            startedAt,
            durationMs: Date.now() - startedAt,
          }
        } else {
          try {
            result = await tools.execute(call.name, call.arguments, toolOptions)
          } catch (error) {
            result = {
              ok: false,
              name: call.name,
              callId: call.id,
              input: call.arguments,
              error: toToolError(error),
              startedAt,
              durationMs: Date.now() - startedAt,
            }
          }
        }
        await recorder.stop()
        toolResults.push(result)
        const toolResultPayload: ToolResultPayload = {
          id: call.id,
          toolCallId: call.id,
          name: call.name,
          ok: result.ok,
          durationMs: result.durationMs,
        }
        if (result.output !== undefined) toolResultPayload.output = result.output
        if (result.attachments?.length) toolResultPayload.attachments = result.attachments.map(item => ({ ...item }))
        if (result.error) {
          toolResultPayload.error = {
            name: result.error.name,
            message: result.error.message,
          }
          if (result.error.stack) toolResultPayload.error.stack = result.error.stack
        }
        await session.append('tool/result', toolResultPayload)
        yield { type: 'tool/end', index, turn, step: index, call, result }
        this.ctx.emit('agent/tool-result', {
          ...(this.config.agentId ? { agentId: this.config.agentId } : {}),
          index,
          turn,
          step: index,
          call,
          result,
        })
      }
      if (options.signal?.aborted) {
        finishReason = 'cancelled'
        break
      }

      steps.push({
        index,
        input: copyMessages(llmMessages),
        completion,
        toolResults,
      })

      if (completion.content) {
        output = completion.content
      }
      if (!toolCalls.length) {
        yield* this.settleAttempt(activeAttempt, {
          content: completion.content ?? '',
          ...settledMeta(),
        })
      }

      await session.append('step/end', {
        turn,
        step: index,
        finishReason: completion.finishReason,
        toolCalls: toolCalls.length,
      })
      currentStepIndex = undefined

      await this.ctx.parallel('agent/step-end', {
        ...(this.config.agentId ? { agentId: this.config.agentId } : {}),
        index,
        turn,
        completion,
        calls: toolCalls.map((call, position) => ({ call, result: toolResults[position]! })),
        ...(options.signal ? { signal: options.signal } : {}),
      } satisfies AgentStepEndEvent)

      const nextMessages = this._extendMessages(llmMessages, completion, toolResults)
      const concludesTurn = toolResults.some(result => result.concludesTurn === true)
      let nextStepMessages = await this._claimNextStepMessages()
      if ((toolCalls.length === 0 || concludesTurn) && nextStepMessages.length === 0) {
        finishReason = lengthReached
          ? 'length'
          : completion.finishReason === 'error'
            ? 'error'
            : 'stop'
        const continued = await this._requestContinuation({
          ...(this.config.agentId ? { agentId: this.config.agentId } : {}),
          index,
          turn,
          steps: copySteps(steps),
          finishReason,
        })
        if (!continued) break
        nextStepMessages = continued
      }
      if (index + 1 >= maxTurns && !finalTurnGranted) {
        finalTurnGranted = true
      } else if (index + 1 >= maxTurns + (finalTurnGranted ? 1 : 0)) {
        finishReason = 'max_turns'
        break
      }
      if (toolCalls.length) finishReason = 'tool_calls'
      index += 1
      messages = [...nextMessages, ...nextStepMessages]
      claimedMessages = nextStepMessages.filter(message => message.role === 'user')
    }
    } catch (error) {
      turnError = error
      throw error
    } finally {
      const cancelCause = cancelCauseFromSignal(options.signal)
      if (currentStepIndex !== undefined) {
        const cancelled = options.signal?.aborted
        await session.append('step/end', {
          turn,
          step: currentStepIndex,
          finishReason: cancelled
            ? 'cancelled'
            : turnError
              ? 'error'
              : 'interrupted',
          interrupted: true,
          ...(cancelCause ? { cancelCause } : {}),
          ...(turnError ? { error: toToolError(turnError) } : {}),
        })
      }
      await session.append('turn/end', {
        turn,
        finishReason: turnError
          ? (options.signal?.aborted ? 'cancelled' : 'error')
          : finishReason,
        reason: toTurnEndReason({
          cancelled: options.signal?.aborted ?? false,
          turnError,
          finishReason: turnError
            ? (options.signal?.aborted ? 'cancelled' : 'error')
            : finishReason,
          ...(cancelCause ? { cancelCause } : {}),
        }),
        ...(cancelCause ? { cancelCause } : {}),
        ...(output ? { output } : {}),
        ...(steps.length ? { steps: steps.length } : {}),
        ...(turnError ? { interrupted: true, error: toToolError(turnError) } : {}),
      })
    }

    const runResult: AgentRunResult = {
      input: claimed,
      output,
      finishReason,
      turn,
      steps,
      messages: copyMessages(messages),
    }
    this.ctx.emit('agent/turn-end', {
      input: claimed,
      steps: copySteps(steps),
      messages: copyMessages(messages),
      output,
      finishReason,
    })
    this.ctx.emit('agent/end', {
      input: claimed,
      steps: copySteps(steps),
      messages: copyMessages(messages),
      output,
      finishReason,
    })
    await this.config.hooks?.afterRun?.(runResult, options)

    await this.ctx.parallel('agent/run-completed', {
      session,
      result: runResult,
      ...(this.config.agentId ? { agentId: this.config.agentId } : {}),
      ...(options.signal ? { signal: options.signal } : {}),
    })

    yield { type: 'run/end', run: runResult }
    return runResult
  }

  private async _enforceContextBudget(
    session: SessionLog,
    budget: AgentContextBudget,
    messages: readonly ModelMessage[],
    llm: LLMAdapter | undefined,
    step: number,
    state: { nearEnd?: boolean },
  ): Promise<ModelMessage[]> {
    const limit = budget.limit ?? DEFAULT_CONTEXT_LIMIT
    const compactRatio = budget.compactRatio ?? DEFAULT_CONTEXT_COMPACT_RATIO
    const hardRatio = Math.max(compactRatio, budget.hardRatio ?? DEFAULT_CONTEXT_HARD_RATIO)
    if (limit <= 0 || compactRatio <= 0 || compactRatio > 1) {
      throw new AgentError('invalid context budget: limit must be positive and compactRatio must be in (0, 1]')
    }
    const usage = estimateContextUsage(messages, limit)
    if (usage.ratio < compactRatio) return copyMessages(messages)
    // Between steps of a run, a run that is about to finish keeps its context
    // until the hard ratio. A new run (step 0) always starts compacted.
    if (step > 0 && usage.ratio < hardRatio && budget.nearEnd) {
      if (state.nearEnd === undefined) {
        try {
          state.nearEnd = await budget.nearEnd(copyMessages(messages), usage)
        } catch (error) {
          this.ctx.logger.warn(`context near-end check failed, compacting: ${String(error)}`)
          state.nearEnd = false
        }
      }
      if (state.nearEnd) return copyMessages(messages)
    }
    // Messages already in the Session are what compaction replaces or keeps;
    // input this step has not persisted yet (the new user message) stays.
    const persisted = await session.deriveMessages()
    const head = messages[0]?.role === 'system' && persisted[0]?.role !== 'system' ? 1 : 0
    const pending = canonicalMessages(messages.slice(head, head + persisted.length)) === canonicalMessages(persisted)
      ? messages.slice(head + persisted.length)
      : undefined
    const keepTokens = budget.keepTokens
      ?? Math.max(1, Math.floor(limit * DEFAULT_CONTEXT_RETAIN_RATIO))
    const compactMessages = budget.summarize
      ? await budget.summarize(messages, usage)
      : [{ role: 'system' as const, content: defaultContextSummary(messages, usage) }]
    const summary = compactMessages
      .map(message => `${message.role}: ${message.content}`)
      .join('\n')
    await session.compact({
      keepTokens,
      summary,
      tokensBefore: usage.tokens,
      messages: compactMessages.map(message => copyMessages([message])[0]!),
    })
    if (llm) {
      try {
        await this.ctx.parallel('agent/context-compacted', {
          ...(this.config.agentId ? { agentId: this.config.agentId } : {}),
          summary,
          sourceMessages: copyMessages(messages),
          llm,
        })
      } catch (error) {
        this.ctx.logger.warn(`project memory update failed after compaction: ${String(error)}`)
      }
    }
    // The request continues from the compacted Session: the summary, the
    // retained tail, then the input not yet persisted.
    const next = pending
      ? [...copyMessages(messages.slice(0, head)), ...await session.deriveMessages(), ...copyMessages(pending)]
      : compactMessages.map(message => copyMessages([message])[0]!)
    this.ctx.emit('agent/context-compact', {
      type: 'agent/context-compact',
      messagesBefore: messages.length,
      tokensBefore: usage.tokens,
      limit,
      keepTokens,
      messagesAfter: next.length,
    })
    return next
  }

  private async _persistStepInput(
    session: SessionLog,
    request: Pick<AgentRequestEvent, 'tools' | 'options'>,
    input: readonly ModelMessage[],
    admittedHistory: readonly ModelMessage[],
    requestHeaderOwnsSystem = false,
  ): Promise<void> {
    const surface = await session.deriveMessages()
    const owned = admittedHistory.filter(message => !isUserOrSystemMessage(message))
    let ownerIndex = 0
    for (const message of input) {
      if (isUserOrSystemMessage(message)) continue
      const actual = canonicalMessages([message])
      while (ownerIndex < owned.length && canonicalMessages([owned[ownerIndex]!]) !== actual) {
        ownerIndex += 1
      }
      if (ownerIndex === owned.length) {
        throw new AgentError('request is not reconstructable from session log: unowned or reordered assistant/tool message')
      }
      ownerIndex += 1
    }
    const nextHeader = requestHeaderFor(request, input)
    const previousHeader = session.requestHeader()
    const isFirstRequest = !this._persistedRequest
    const isResume = isFirstRequest && previousHeader !== undefined
    this._persistedRequest = true
    const changedHeader = previousHeader && (
      previousHeader.system !== nextHeader.system
      || JSON.stringify(previousHeader.tools ?? []) !== JSON.stringify(nextHeader.tools ?? [])
      || JSON.stringify(previousHeader.config ?? {}) !== JSON.stringify(nextHeader.config ?? {})
    )
    if (isFirstRequest) {
      await session.append('request/header', {
        reason: this._seriesStarted
          ? 'series'
          : isResume
            ? 'resume'
            : 'initial',
        ...nextHeader,
        ...(this._seriesStarted ? { startsSeries: true } : {}),
      })
    } else if (changedHeader) {
      await session.append('request/header', {
        reason: this._seriesStarted ? 'change-series' : 'change',
        ...nextHeader,
        startsSeries: true,
      })
    } else if (this._seriesStarted) {
      await session.append('request/header', {
        reason: 'series',
        ...nextHeader,
        startsSeries: true,
      })
    }
    this._seriesStarted = false

    const nextContext: RequestContextPayload = {
      ...(request.options.provider ? { provider: request.options.provider } : {}),
      ...(request.options.model ? { model: request.options.model } : {}),
    }
    if (request.options.contextWindow !== undefined) {
      nextContext.contextWindow = request.options.contextWindow
    }
    const llmService = this.ctx.get('llm', false)
    const routeCapacity = llmService?.routeCapacity()
    if (nextContext.contextWindow === undefined && routeCapacity?.contextWindow !== undefined) {
      nextContext.contextWindow = routeCapacity.contextWindow
    }
    const previousContext = session.requestContext()
    if (
      previousContext?.provider !== nextContext.provider
      || previousContext?.model !== nextContext.model
      || previousContext?.contextWindow !== nextContext.contextWindow
      || (previousContext === undefined)
    ) {
      await session.append('request/context', nextContext)
    }
    const isSurfacePrefix = (messages: readonly ModelMessage[]): boolean =>
      canonicalMessages(messages.slice(0, surface.length)) === canonicalMessages(surface)
    // A run-scoped leading system may be owned by the effective header. Remove
    // exactly that prefix when the remaining transcript extends the surface.
    const requested = (surface.length > 0 || requestHeaderOwnsSystem)
      && input[0]?.role === 'system'
      && input[0].content === nextHeader.system
      && isSurfacePrefix(input.slice(1))
      ? input.slice(1)
      : input
    const tail = requested.slice(surface.length)
    if (isSurfacePrefix(requested) && tail.every(isUserOrSystemMessage)) {
      for (const message of tail) {
        if (message.role === 'user') {
          await session.append('user/message', {
            content: message.content,
            ...(message.name ? { name: message.name } : {}),
            ...(message.attachments?.length ? { attachments: message.attachments } : {}),
          })
        } else {
          await session.append('system/message', {
            content: message.content,
            ...(message.name ? { name: message.name } : {}),
          })
        }
      }
      return
    }
    // Rewrites replace the model surface through the existing append-only
    // checkpoint operation. Prior raw history and its message owners remain.
    const nodes = await session.surfaceEvents()
    await session.append('checkpoint', {
      messages: copyMessages(requested),
      surfaceOp: { op: 'replace', start: nodes[0]!.seq, end: nodes.at(-1)!.seq },
    })
  }

  private async _assertReplayable(
    session: SessionLog,
    request: Pick<AgentRequestEvent, 'tools' | 'options'>,
    input: readonly ModelMessage[],
  ): Promise<void> {
    const reconstructed = await session.deriveMessages()
    const actual = canonicalMessages(input)
    const replay = canonicalMessages(reconstructed)
    const header = session.requestHeader()
    const headerOwnsPrefix = input[0]?.role === 'system'
      && input[0].content === header?.system
      && canonicalMessages(input.slice(1)) === replay
    if (actual !== replay && !headerOwnsPrefix) {
      throw new AgentError(
        `request is not reconstructable from session log: actual=${actual} replay=${replay}`,
      )
    }
    const expected = requestHeaderFor(request, input)
    if (
      header?.system !== expected.system
      || JSON.stringify(header?.tools ?? []) !== JSON.stringify(expected.tools ?? [])
      || JSON.stringify(header?.config ?? {}) !== JSON.stringify(expected.config ?? {})
    ) {
      throw new AgentError('request envelope is not reconstructable from session log')
    }
  }

  /**
   * Give plugins the last word before a turn stops: one may steer the model on
   * (the continuation nudge does), and whatever it queued is claimed here.
   * Returns the messages to continue with — empty when a listener only said to
   * keep going — or undefined when the turn really stops.
   */
  private async _requestContinuation(payload: AgentTurnStoppingEvent): Promise<ModelMessage[] | undefined> {
    const keepGoing = await this.ctx.serial('agent/turn-stopping', payload) as unknown
    if (keepGoing) return []
    const claimed = await this._claimNextStepMessages()
    return claimed.length ? claimed : undefined
  }

  private async _claimNextStepMessages(): Promise<ModelMessage[]> {
    const inputs = await this.config.claimNextStep?.() ?? []
    return inputs.flatMap(input => input.messages?.length
      ? copyMessages(input.messages)
      : input.text || input.attachments?.length
        ? [{
          role: 'user' as const,
          content: input.text ?? '',
          ...(input.attachments?.length ? { attachments: input.attachments } : {}),
        }]
        : input.context !== undefined
          ? [{ role: 'user' as const, content: stringify(input.context) }]
          : [])
  }

  private async _initialMessages(input: AgentInput): Promise<ModelMessage[]> {
    const promptService = this.ctx.get('systemPrompt', false)
    const assembled = promptService
      ? (await promptService.assemble({}, this.ctx)).text.trim()
      : undefined
    const systemPrompt = assembled || this.inbox.injected().get('agentSystem')
    const systemMessage = typeof systemPrompt === 'string' && systemPrompt
      ? [{ role: 'system' as const, content: systemPrompt }]
      : []
    if (input.messages?.length) {
      const first = input.messages[0]
      if (first?.role === 'system') return copyMessages(input.messages)
      return [...systemMessage, ...copyMessages(input.messages)]
    }
    if (input.text || input.attachments?.length) {
      return [...systemMessage, {
        role: 'user',
        content: input.text ?? '',
        ...(input.attachments?.length ? { attachments: input.attachments.map(item => ({ ...item })) } : {}),
      }]
    }
    return systemMessage
  }

  private _extendMessages(
    messages: readonly ModelMessage[],
    completion: LLMCompletion,
    toolResults: readonly ToolResult[],
  ): ModelMessage[] {
    const next = copyMessages(messages)
    const assistant: ModelMessage = {
      role: 'assistant',
      content: completion.content ?? '',
    }
    if (completion.reasoning) assistant.reasoning = completion.reasoning
    const toolCalls = completion.toolCalls ?? []
    if (toolCalls.length) {
      assistant.tool_calls = toolCalls.map(call => ({
        id: call.id,
        name: call.name,
        arguments: call.arguments,
      }))
    }
    if (assistant.content || toolCalls.length) next.push(assistant)

    for (const call of toolCalls) {
      const result = toolResults.find(candidate => candidate.callId === call.id)
      const content = result
        ? renderToolResult(result)
        : 'error: missing tool result'
      const message: ModelMessage = {
        role: 'tool',
        content,
        tool_call_id: call.id,
      }
      message.name = result?.name ?? call.name
      if (result?.attachments?.length) message.attachments = result.attachments.map(item => ({ ...item }))
      if (result && !result.ok) {
        message.toolOk = false
        if (result.error) {
          message.toolError = {
            name: result.error.name,
            message: result.error.message,
          }
        }
      }
      next.push(message)
    }
    return next
  }

  private _session(): SessionLog {
    if (this.config.session) return this.config.session
    const session = this.ctx.get('session', false) ?? this._inherited('session')
    if (!session) throw new AgentError('session service is required')
    return session
  }

  private _tools(): ToolsService {
    const tools = this.ctx.get('tools', false) ?? this._inherited('tools')
    if (!tools) throw new AgentError('tools service is required')
    return tools
  }

  /**
   * `ctx.get` answers from this context's exact isolate scope. The property form
   * also walks the parent fiber chain, which is how a nested Agent context
   * inherits the services of the runtime that created it. Both are needed, in
   * that order.
   */
  private _inherited<K extends keyof ServiceMap>(name: K): ServiceMap[K] | undefined {
    try {
      return this.ctx[name]
    } catch {
      return undefined
    }
  }

  private async _resolveAvailableTools(): Promise<readonly ToolDefinition[]> {
    // Executable tools are the single source of truth. When a system-prompt
    // service is mounted, its assembled schema list narrows what the model
    // sees — but only to tools that are actually registered, so a declared
    // schema can never advertise an un-executable stub.
    const executable = this._tools().list()
    const promptService = this.ctx.get('systemPrompt', false)
    if (!promptService) return executable
    const schemas = await promptService.toolSchemas()
    if (!schemas.length) return executable
    const byName = new Map(executable.map(tool => [tool.schema.name, tool] as const))
    return schemas
      .map(schema => byName.get(schema.name))
      .filter((tool): tool is ToolDefinition => tool !== undefined)
  }

  private _llm(): LLMAdapter | undefined {
    if (this.config.llm) return this.config.llm
    const service = this.ctx.get('llm', false)
    return service?.current()
  }
}

function defaultContextSummary(
  messages: readonly ModelMessage[],
  usage: { tokens: number },
): string {
  const latest = messages.at(-1)
  const latestText = latest ? `${latest.role}: ${latest.content}` : 'none'
  return [
    'Earlier context was compacted.',
    `Messages before compaction: ${messages.length}.`,
    `Tokens before compaction: ${usage.tokens}.`,
    `Latest message: ${latestText}`,
  ].join(' ')
}

function copySteps(steps: readonly AgentStep[]): readonly AgentStep[] {
  return steps.map(step => ({
    index: step.index,
    input: copyMessages(step.input),
    completion: copyCompletion(step.completion),
    toolResults: step.toolResults.map(result => ({ ...result })),
  }))
}

function completionFromStreamEvents(events: readonly LLMStreamEvent[]): LLMCompletion {
  let content = ''
  let reasoning = ''
  const calls = new Map<number, LLMToolCall>()
  let finishReason: AgentFinishReason = 'error'
  let usage: ModelUsage | undefined
  for (const event of events) {
    if (event.type === 'message_delta') {
      content += event.delta
    } else if (event.type === 'reasoning_delta') {
      reasoning += event.delta
    } else if (event.type === 'toolcall_end') {
      calls.set(event.index, {
        id: event.id,
        name: event.name,
        arguments: event.arguments,
      })
    } else if (event.type === 'message_stop') {
      finishReason = event.finishReason
      if (event.usage) usage = event.usage
    }
  }
  const completion: LLMCompletion = { finishReason }
  if (content) completion.content = content
  if (reasoning) completion.reasoning = reasoning
  if (calls.size) completion.toolCalls = [...calls.values()]
  if (usage) completion.usage = usage
  return completion
}

async function consumeAgentStream(
  stream: AsyncGenerator<AgentStreamEvent, AgentRunResult, void>,
): Promise<AgentRunResult> {
  const iterator = stream[Symbol.asyncIterator]()
  while (true) {
    const next = await iterator.next()
    if (next.done) return next.value
  }
}

export const agent = {
  name: 'agent',
  inject: ['session', 'tools'],
  apply: (ctx: Context, config: AgentConfig = {}) => {
    const service = new AgentService(ctx, config)
    ctx.provide('agent', service)
    ctx.provide('agentLoop', (input?: AgentInput, options?: AgentRunOptions) => service.run(input, options))
    ctx.provide('inbox', service.inbox)
    return () => {}
  },
}

export const name = '@tnega/agent'

/**
 * Keeps the tail of a running tool's output durable, at most every
 * {@link TOOL_OUTPUT_INTERVAL_MS}, as a log-only `meta` record. Crash repair
 * reads the latest one, so an interrupted call can say what it printed.
 */
class ToolOutputRecorder {
  private tail = ''
  private dropped = false
  private timer: ReturnType<typeof setTimeout> | undefined
  private writing: Promise<unknown> = Promise.resolve()
  private stopped = false

  constructor(private readonly session: SessionLog, private readonly toolCallId: string) {}

  push(output: string): void {
    if (this.stopped || !output) return
    this.tail += output
    if (this.tail.length > TOOL_OUTPUT_TAIL_CHARS) {
      this.tail = this.tail.slice(-TOOL_OUTPUT_TAIL_CHARS)
      this.dropped = true
    }
    if (this.timer) return
    this.timer = setTimeout(() => {
      this.timer = undefined
      this.writing = this.session.append('meta', {
        kind: TOOL_OUTPUT_META_KIND,
        toolCallId: this.toolCallId,
        output: this.tail,
        ...(this.dropped ? { truncated: true } : {}),
      }).catch(() => undefined)
    }, TOOL_OUTPUT_INTERVAL_MS)
    this.timer.unref?.()
  }

  /** The result supersedes the progress: stop before it is recorded. */
  async stop(): Promise<void> {
    this.stopped = true
    if (this.timer) clearTimeout(this.timer)
    this.timer = undefined
    await this.writing
  }
}

const TOOL_OUTPUT_TAIL_CHARS = 2_000
const TOOL_OUTPUT_INTERVAL_MS = 2_000
